import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type { TServerContactProperty } from "@/schemas/contactProperty.schema"
import type { ColumnConfig } from "@/features/PowerDialer/CentralPanel/CustomizeColumnsModal"
import type { EnrichedDialerData } from "@/features/PowerDialer/CentralPanel/ListTable"
import type { TDialerList } from "@/schemas/user.schema"
import type { PowerDialerState, PowerDialerActions } from "./types"
import { computeDialerListValues, getStorageKey, getDefaultVisibleColumns, getDefaultColumnOrder, loadColumnConfiguration, saveColumnConfiguration } from "./helpers"

const initialState: PowerDialerState = {
  // UI state
  isWaitingForUploadData: false,
  uploadResetCallback: null,

  // Table state
  containerWidth: 0,
  containerLeft: 0,

  // Column configuration state
  currentListId: null,
  availableColumns: [],
  contactProperties: [],
  enabledColumnIds: [],
  columnOrder: [],

  // Filter & sort state
  currentFilters: null,
  currentSorting: null,

  // Expanded rows state
  expandedContactIds: [],

  // Dialer list data
  dialerListData: null,

  // Cached computed values
  cachedDialerContactIds: [],
  cachedDialerListItems: [],
  cachedPastCallContactIds: [],
  cachedPastCallListItems: [],
}

export const usePowerDialerStore = create<PowerDialerState & PowerDialerActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // UI actions
      setWaitingForUploadData: (waiting: boolean) => {
        set({ isWaitingForUploadData: waiting })
      },

      setUploadResetCallback: (callback: (() => Promise<void>) | null) => {
        set({ uploadResetCallback: callback })
      },

      // Table actions
      setContainerDimensions: (width: number, left: number) => {
        set({ containerWidth: width, containerLeft: left })
      },

      // Column configuration actions
      initializeColumns: (listId: string | null, availableColumns: string[], contactProperties: TServerContactProperty[]) => {
        const config = loadColumnConfiguration(listId, availableColumns)
        set({
          currentListId: listId,
          availableColumns,
          contactProperties,
          enabledColumnIds: config.enabledIds,
          columnOrder: config.columnOrder,
        })
      },

      updateColumns: (newColumns: ColumnConfig[]) => {
        const state = get()
        const sortedColumns = [...newColumns].sort((a: ColumnConfig, b: ColumnConfig) => a.order - b.order)
        const enabledColumnIds = sortedColumns.filter((col: ColumnConfig) => col.enabled).map((col: ColumnConfig) => col.id)
        const newColumnOrder = sortedColumns.map((col: ColumnConfig) => ({ id: col.id, order: col.order }))

        set({ enabledColumnIds, columnOrder: newColumnOrder })
        saveColumnConfiguration(state.currentListId, enabledColumnIds, newColumnOrder)
      },

      updateColumnOrder: (newOrder: string[]) => {
        const state = get()
        const columns = state.getColumns()
        const updatedColumns = columns.map((col: ColumnConfig) => {
          const orderIndex = newOrder.indexOf(col.id)
          return {
            ...col,
            order: orderIndex !== -1 ? orderIndex : col.order,
          }
        })
        state.updateColumns(updatedColumns)
      },

      hideColumn: (columnId: string) => {
        const state = get()
        const newEnabled = state.enabledColumnIds.filter((id) => id !== columnId)
        set({ enabledColumnIds: newEnabled })
        saveColumnConfiguration(state.currentListId, newEnabled, state.columnOrder)
      },

      resetColumns: () => {
        const state = get()
        const { currentListId, availableColumns } = state

        // Remove stored config from localStorage
        if (typeof window !== "undefined" && currentListId) {
          const storageKey = getStorageKey(currentListId)
          localStorage.removeItem(storageKey)
        }

        // Reset to default columns with proper ordering
        const defaultVisible = getDefaultVisibleColumns(availableColumns)
        const newColumnOrder = getDefaultColumnOrder(availableColumns)

        set({
          enabledColumnIds: defaultVisible,
          columnOrder: newColumnOrder,
        })
      },

      // Filter & sort actions
      setFilters: (filters: any | null) => {
        set({ currentFilters: filters })
      },

      setSorting: (sorting: { [key: string]: 1 | -1 } | null) => {
        set({ currentSorting: sorting })
      },

      // Expanded rows actions
      setExpandedContactIds: (ids: string[]) => {
        const uniqueIds = Array.from(new Set(ids))
        set({ expandedContactIds: uniqueIds })
      },

      toggleExpandedContact: (id: string) => {
        const state = get()
        const currentIds = state.expandedContactIds
        const index = currentIds.indexOf(id)
        if (index >= 0) {
          set({ expandedContactIds: currentIds.filter((existingId) => existingId !== id) })
        } else {
          set({ expandedContactIds: [...currentIds, id] })
        }
      },

      clearExpandedContacts: () => {
        set({ expandedContactIds: [] })
      },

      addExpandedContact: (id: string) => {
        const state = get()
        const currentIds = state.expandedContactIds
        if (!currentIds.includes(id)) {
          set({ expandedContactIds: [...currentIds, id] })
        }
      },

      removeExpandedContact: (id: string) => {
        const state = get()
        set({ expandedContactIds: state.expandedContactIds.filter((existingId) => existingId !== id) })
      },

      // Dialer list data actions
      setDialerListData: (data: TDialerList | null | undefined) => {
        const computed = computeDialerListValues(data)
        set({
          dialerListData: data,
          ...computed,
        })
      },

      recomputeDialerListCache: () => {
        const state = get()
        const computed = computeDialerListValues(state.dialerListData)
        set(computed)
      },

      updateContactIdInDialerList: (tempId: string, newContactId: number) => {
        const state = get()
        const currentData = state.dialerListData
        if (!currentData?.list) return

        const updatedList = currentData.list.map((item) => {
          if (item._id?.toString() === tempId) {
            return {
              ...item,
              id: newContactId,
            }
          }
          return item
        })

        const updatedData: TDialerList = {
          ...currentData,
          list: updatedList,
        }

        // Use setDialerListData to update and recompute caches
        state.setDialerListData(updatedData)
      },

      // Reset state
      reset: () => {
        set(initialState)
      },

      // Selectors (computed values from Legend State)
      getColumns: (): ColumnConfig[] => {
        const state = get()
        const { availableColumns, contactProperties, enabledColumnIds, columnOrder } = state

        const orderMap = new Map(columnOrder.map((item) => [item.id, item.order]))
        const propertyMap = new Map(contactProperties.map((prop) => [prop.key, prop.label]))
        const enabledSet = new Set(enabledColumnIds)

        return availableColumns.map((columnId) => ({
          id: columnId,
          name: propertyMap.get(columnId) || columnId,
          enabled: enabledSet.has(columnId),
          order: orderMap.get(columnId) ?? 999,
        }))
      },

      getVisibleColumns: (): ColumnConfig[] => {
        const state = get()
        return state
          .getColumns()
          .filter((col: ColumnConfig) => col.enabled)
          .sort((a: ColumnConfig, b: ColumnConfig) => a.order - b.order)
      },

      getCurrentColumnOrder: (): string[] => {
        const state = get()
        return state.getVisibleColumns().map((col: ColumnConfig) => col.id)
      },

      getTotalContacts: (): number => {
        const state = get()
        return state.cachedDialerContactIds?.length || 0
      },

      getDialerContactIds: (): Array<string | null> => {
        const state = get()
        return state.cachedDialerContactIds
      },

      getDialerListItems: (): EnrichedDialerData[] => {
        const state = get()
        return state.cachedDialerListItems
      },

      getPastCallContactIds: (): Array<string | null> => {
        const state = get()
        return state.cachedPastCallContactIds
      },

      getPastCallListItems: (): EnrichedDialerData[] => {
        const state = get()
        return state.cachedPastCallListItems
      },

      getHasSelectedList: (): boolean => {
        const state = get()
        return state.cachedDialerContactIds.length > 0
      },

      getExpandedContactIdsArray: (): string[] => {
        const state = get()
        return state.expandedContactIds
      },

      getVisibleExpandedIds: (): string[] => {
        const state = get()
        const expandedIds = state.expandedContactIds
        const currentItems = state.getDialerListItems()
        const pastCallItems = state.getPastCallListItems()

        // Get all visible contact IDs
        const visibleIds = new Set<string>()
        ;[...currentItems, ...pastCallItems].forEach((item) => {
          if (item.ContactPersonId) {
            visibleIds.add(item.ContactPersonId)
          }
          // Also add fallback IDs for records without ContactPersonId
          if (item.id) {
            visibleIds.add(`record-${item.id}`)
          }
        })

        // Return only expanded IDs that are currently visible
        return expandedIds.filter((id) => visibleIds.has(id))
      },
    }),
    {
      name: "power-dialer-store",
    }
  )
)

export type { PowerDialerStore } from "./types"
